/**
 * Field Label Resolver Utility
 * 
 * This utility converts server field values (with IDs) to human-readable labels.
 * It provides a centralized, performant way to resolve field labels across the application.
 * 
 * @example
 * ```typescript
 * // Create a resolver with inventaire data
 * const resolver = createFieldResolver(inventaire);
 * 
 * // Simple field resolution - no need to pass values!
 * resolver.getFieldLabel('stade_dev') // → "Juvénile"
 * resolver.getFieldLabel('port_arbre') // → "Semi-libre"
 * resolver.getFieldLabel('caract_pied') // → "Terre, litière, Protection"
 * 
 * // Get multiple fields at once
 * const labels = resolver.getMultipleFieldLabels(['stade_dev', 'port_arbre', 'domaine'])
 * // → { stade_dev: "Juvénile", port_arbre: "Semi-libre", domaine: "Espace public" }
 * 
 * // Legacy API still available
 * getFieldLabel('stade_dev', 'jeune') // → "Juvénile"
 * ```
 * 
 * @performance
 * - Uses Map for caching (O(1) lookups)
 * - First call: ~1-2ms (creates cache)
 * - Subsequent calls: <0.1ms (cache hit)
 * 
 * @author Dendromap Team
 */
import {
  ARBRE_FIELDS_PRINT_CONFIG,
  AESTHETIC_INDEX_TAB,
  HEALTH_INDEX_TAB,
  LOCATION_INDEX_TAB,
  BevaHelper,
  epaysageFields as epaysageFieldConfigs,
  essenceFields as essenceFieldConfigs,
} from '@repo/field-configs';
import { Arbre, Epaysage, Essence } from '@repo/types';

/**
 * All possible arbre field names including computed BEVA fields
 * This type ensures full type safety when working with arbre fields
 */
export type ArbreFieldName =
  | keyof Arbre
  | 'variety_grade'      // Computed BEVA field: valeur intrinsèque - indice espèce
  | 'health_index'       // Computed BEVA field: valeur intrinsèque - indice sanitaire
  | 'aesthetic_index'    // Computed BEVA field: valeur intrinsèque - indice esthétique
  | 'location_index';    // Computed BEVA field: valeur intrinsèque - indice situation

/**
 * Mapping between server field names (snake_case) and print config names (kebab-case)
 * This creates a direct, fast lookup without needing to search
 * 
 * Complete mapping for all arbre (tree) fields - 40+ fields mapped
 */
const SERVER_FIELD_TO_CONFIG_MAP: Record<string, string> = {
  // ========================================
  // Caractéristiques du sujet
  // ========================================
  caract_pied: 'au-pied-de-l-arbre',
  caract_tronc: 'caracteristiques-du-sujet-tronc',
  port_arbre: 'port-de-larbre',
  stade_dev: 'stade-de-developpement',
  critere: 'critere-remarquable',

  // ========================================
  // État sanitaire
  // ========================================
  etat_san_collet: 'etat-sanitaire-systme-racinaire-collet',
  etat_san_tronc: 'etat-sanitaire-tronc',
  etat_san_houppier: 'etat-sanitaire-houppier',
  etat_san_general: 'etat-sanitaire-general',

  // ========================================
  // Risques
  // ========================================
  risque: 'risque-de-rupture',
  risque_general: 'risque-de-rupture',

  // ========================================
  // Environnement
  // ========================================
  implantation: 'implantation',
  domaine: 'domaine',
  proximite: 'proximite-directe',
  dict: 'dict',
  nuisance: 'nuisances-constatees',
  taux_freq: 'taux-de-frquentation',
  type_passage: 'type-de-passage',
  accessibilite: 'accessibilite',

  // ========================================
  // Préconisations et Travaux
  // ========================================
  abattage: 'preco-travaux-abattage',
  travaux_collet_multiple: 'preco-travaux-collet',
  travaux_tronc_multiple: 'preco-travaux-tronc',
  travaux_houppier_multiple: 'preco-travaux-houppier',
  date_travaux: 'preco-travaux-date-travaux',
  travaux_commentaire: 'preco-travaux-com',
  com_pro_visite: 'preco-travaux-com',

  // ========================================
  // Valeur intrinsèque (BEVA)
  // ========================================
  variety_grade: 'valeur-intrinseque-indice-espece',
  health_index: 'valeur-intrinseque-indice-sanitaire',
  aesthetic_index: 'valeur-intrinseque-indice-esthetique',
  location_index: 'valeur-intrinseque-indice-situation',

  // ========================================
  // Champignons et nuisibles (arrays of IDs)
  // ========================================
  etat_san_collet_champignons: 'etat-sanitaire-systme-racinaire-collet_champignons-lignivores',
  etat_san_tronc_champignons: 'etat-sanitaire-tronc_champignons-lignivores',
  etat_san_houppier_champignons: 'etat-sanitaire-houppier_champignons-lignivores',
  etat_san_tronc_nuisibles: 'etat-sanitaire-tronc_nuisibles',
  etat_san_houppier_nuisibles: 'etat-sanitaire-houppier_nuisibles',

};
const SERVER_FIELD_TO_CONFIG_MAP_EPAYSAGE: Record<string, string> = {
  milieu: 'milieu',
  sol: 'sol',
  nuissance: 'nuisances-constatees',
  situation: 'valeur-intrinseque-indice-situation',
  domaine: 'domaine',
  proximite: 'proximite-directe',
  accessibilite: 'accessibilite',
  taux_freq: 'taux-de-frquentation',
  type_passage: 'type-de-passage',
  risque_general: 'risque-general',
  type_espace: 'type-espace',
  etat_san_general: 'etat-sanitaire-general',
  location_index: 'valeur-intrinseque-indice-situation',
};
const SERVER_FIELD_TO_CONFIG_MAP_ESSENCE: Record<string, string> = {

  stade_dev: 'stade-de-developpement',
  port_arbre: 'port-de-larbre',
  etat_san_general: 'etat-sanitaire-general',
  risque: 'risque-de-rupture',
  critere: 'critere-remarquable',
  critere_other: 'critere-remarquable-autre',
  travaux: 'preconisation-de-travaux',
  date_travaux: 'preco-travaux-date-travaux',
  date_pro_visite: 'preco-travaux-prochaine-visite',
};

/**
 * Cached field config lookup for better performance
 */
const fieldConfigCache = new Map<string, any>();

function getFieldConfig(fieldName: string) {
  if (fieldConfigCache.has(fieldName)) {
    return fieldConfigCache.get(fieldName);
  }

  const config = ARBRE_FIELDS_PRINT_CONFIG.find(f => f.name === fieldName);

  fieldConfigCache.set(fieldName, config);
  return config;
}
function getFieldConfigEpaysage(fieldName: string) {
  if (fieldConfigCache.has(fieldName)) {
    return fieldConfigCache.get(fieldName);
  }

  const config = epaysageFieldConfigs[fieldName as keyof typeof epaysageFieldConfigs];
  fieldConfigCache.set(fieldName, config);
  return config;
}
function getFieldConfigEssence(fieldName: keyof typeof SERVER_FIELD_TO_CONFIG_MAP_ESSENCE) {
  if (fieldConfigCache.has(fieldName)) {
    return fieldConfigCache.get(fieldName);
  }

  const config = essenceFieldConfigs[fieldName as keyof typeof essenceFieldConfigs];
  fieldConfigCache.set(fieldName, config);
  return config;
}
/**
 * Converts a server field value to its human-readable label
 * 
 * @param serverFieldName - The server field name (e.g., 'caract_pied', 'stade_dev')
 * @param value - The field value (can be string, array, or object)
 * @param options - Optional data for dynamic lookups (champignons, especes, nuisibles)
 * @returns Human-readable label(s) or the original value if no mapping found
 */
export function getFieldLabel(
  serverFieldName: ArbreFieldName,
  value: any,
  options?: {
    champignons?: any[];
    especes?: any[];
    nuisibles?: any[];
  }
): string | null {
  // Return empty string for null/undefined
  if (value === null || value === undefined || value === '') {
    return '';
  }



  // Special handling for champignons fields (dynamic lookup from champignons array)
  if (serverFieldName.includes('champignons') && Array.isArray(value) && options?.champignons) {
    return value
      .map(id => {
        const champignon = options.champignons?.find((c: any) => c.id === Number(id) || c.id === id);
        return champignon?.name || id;
      })
      .filter(Boolean)
      .join(', ');
  }

  // Special handling for nuisibles fields (dynamic lookup from nuisibles array)
  if (serverFieldName.includes('nuisibles') && Array.isArray(value) && options?.nuisibles) {
    return value
      .map(id => {
        const nuisible = options.nuisibles?.find((n: any) => n.id === Number(id) || n.id === id);
        return nuisible?.name || id;
      })
      .filter(Boolean)
      .join(', ');
  }

  if (serverFieldName === 'location_index') {
    return BevaHelper.getDisplayNameIndex(value, LOCATION_INDEX_TAB);
  }

  if (serverFieldName == "health_index") {
    return BevaHelper.getDisplayNameIndex(value, HEALTH_INDEX_TAB);
  }
  if (serverFieldName === "aesthetic_index") {
    return BevaHelper.getDisplayNameIndex(value, AESTHETIC_INDEX_TAB);
  }

  // Get the config field name from server field name
  const configFieldName = SERVER_FIELD_TO_CONFIG_MAP[serverFieldName];

  // If no mapping found, return the value as string
  if (!configFieldName) {
    return convertValueToString(value);
  }

  // Get field configuration
  const fieldConfig = getFieldConfig(configFieldName);

  // If no field config found, return the value as string
  if (!fieldConfig) {
    return convertValueToString(value);
  }
  if (serverFieldName == 'implantation') {
    fieldConfig.options.push({ id: 'alignement-monospécifique', label: 'Alignement monospécifique' }, { id: 'alignement-heterogenes', label: 'Alignement Hétérogènes' })
    fieldConfig.options.push({
      "id": "alignement-heterogenes",
      "label": "Alignement Hétérogènes"
    })
  }

  if (configFieldName == 'etat-sanitaire-general') {
    console.log(configFieldName, { fieldConfig }, value);
  }


  // Handle multiselect fields (arrays)
  if (fieldConfig.type === 'multiselect' && Array.isArray(value)) {

    return value
      .map(v => {
        const option = fieldConfig.options?.find((opt: any) => opt.id?.toString()?.toLowerCase() === v?.toString()?.toLowerCase());
        return option?.label || v;
      })
      .filter(Boolean)
      .join(', ');
  }

  // Handle radio/select fields (single value)
  if ((fieldConfig.type === 'radio' || fieldConfig.type === 'select') && fieldConfig.options) {
    const option = fieldConfig.options.find((opt: any) => opt.id?.toString()?.toLowerCase() === value?.toString()?.toLowerCase());
    return option?.label || convertValueToString(value);
  }

  // Handle text and number fields
  if (fieldConfig.type === 'text' || fieldConfig.type === 'number' || fieldConfig.type === 'textarea') {
    return convertValueToString(value);
  }

  // Default: return the value as string
  return convertValueToString(value);
}

export function getFieldLabelEpaysage(serverFieldName: keyof Epaysage, value: any): string {
  // Return empty string for null/undefined
  console.log({ serverFieldName, value });
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Get the config field name from server field name
  const configFieldName = SERVER_FIELD_TO_CONFIG_MAP_EPAYSAGE[serverFieldName];

  if (!configFieldName) {

    return convertValueToString(value);
  }

  const fieldConfig = getFieldConfigEpaysage(configFieldName);

  if (!fieldConfig) {
    return convertValueToString(value);
  }
  return convertValueToLabel(value, fieldConfig);
}

export function getFieldLabelEssence(serverFieldName: keyof Essence, value: any): string {
  // Return empty string for null/undefined
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const configFieldName = SERVER_FIELD_TO_CONFIG_MAP_ESSENCE[serverFieldName];
  if (!configFieldName) {
    return convertValueToString(value);
  }
  const fieldConfig = getFieldConfigEssence(configFieldName);
  if (!fieldConfig) {
    return convertValueToString(value);
  }
  return convertValueToLabel(value, fieldConfig);
}
export function convertValueToLabel(value: any, fieldConfig: any): string {
  if (fieldConfig.type === 'multiple' && Array.isArray(value)) {
    return value.map(v => fieldConfig.values?.find((opt: any) => opt.id == v)?.label || v).filter(Boolean).join(', ');
  }
  if (fieldConfig.type === 'scalaire') {
    return fieldConfig.values?.find((opt: any) => opt.id == value)?.label || convertValueToString(value);
  }
  return convertValueToString(value);
}
/**
 * Safely converts any value to string
 */
function convertValueToString(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Batch convert multiple fields at once for better performance
 */
export function getMultipleFieldLabels(
  fields: Array<{ serverFieldName: ArbreFieldName; value: any }>,
  options?: {
    champignons?: any[];
    especes?: any[];
    nuisibles?: any[];
  }
): Record<string, string> {
  const result: Record<string, string | null> = {};

  for (const { serverFieldName, value } of fields) {
    result[serverFieldName] = getFieldLabel(serverFieldName, value, options);
  }

  return result as Record<string, string>;
}

/**
 * Get the human-readable field label name (not the value, but the field name itself)
 */
export function getFieldLabelName(serverFieldName: ArbreFieldName): string {
  const configFieldName = SERVER_FIELD_TO_CONFIG_MAP[serverFieldName];
  if (!configFieldName) return serverFieldName;

  const fieldConfig = getFieldConfig(configFieldName);
  return fieldConfig?.label || serverFieldName;
}

/**
 * Interface for the field resolver returned by createFieldResolver
 */
export interface FieldResolver {
  /**
   * Get the human-readable label for a field
   * @param serverFieldName - The server field name (e.g., 'stade_dev', 'port_arbre')
   */
  getFieldLabel: (serverFieldName: ArbreFieldName) => string | null;

  /**
   * Get labels for multiple fields at once
   * @param serverFieldNames - Array of server field names
   */
  getMultipleFieldLabels: (serverFieldNames: ArbreFieldName[]) => Record<string, string>;

  /**
   * Get the human-readable field name (not the value)
   * @param serverFieldName - The server field name
   */
  getFieldLabelName: (serverFieldName: ArbreFieldName) => string;

  getRisqueLabel: (risque: any) => Record<string, string>;
}
export interface EpaysageFieldResolver {
  getFieldLabel: (serverFieldName: keyof Epaysage) => string;
}
export interface EssenceFieldResolver {
  getFieldLabel: (serverFieldName: keyof Essence) => string;
}

export interface FieldResolverOptions {
  champignons?: any[];
  nuisibles?: any[];
  especes?: any[];
}

/**
 * Creates a field resolver with captured inventaire data
 * 
 * This is the recommended way to use the field resolver - it creates a closure
 * that captures all the data from your inventaire, so you don't need to pass
 * values every time you want to resolve a field.
 * 
 * @param inventaire - The inventaire object containing arbre, epaysage, and related data
 * @returns A resolver object with methods to resolve field labels
 * 
 * @example
 * ```typescript
 * const resolver = createFieldResolver(inventaire);
 * 
 * // No need to pass values - they're captured from inventaire
 * const stadeLabel = resolver.getFieldLabel('stade_dev');
 * const portLabel = resolver.getFieldLabel('port_arbre');
 * 
 * // Get multiple at once
 * const labels = resolver.getMultipleFieldLabels(['stade_dev', 'port_arbre', 'critere']);
 * ```
 */
export function createFieldResolver(inventaire: any, _options?: FieldResolverOptions): FieldResolver {
  const arbre = inventaire?.arbre;
  const epaysage = inventaire?.epaysage;
  const options = {
    champignons: _options?.champignons ?? [],
    nuisibles: _options?.nuisibles ?? [],
    especes: _options?.especes ?? [],
  };

  return {
    /**
     * Get the label for a single field
     */
    getFieldLabel: (serverFieldName: ArbreFieldName): string | null => {
      // Get the value from arbre or epaysage
      let value = arbre?.[serverFieldName as keyof typeof arbre] ?? epaysage?.[serverFieldName] ?? inventaire?.[serverFieldName];

      // Use the base getFieldLabel function
      return getFieldLabel(serverFieldName, value, options);
    },

    /**
     * Get labels for multiple fields
     */
    getMultipleFieldLabels: (serverFieldNames: ArbreFieldName[]): Record<string, string> => {
      const result: Record<string, string | null> = {};

      for (const fieldName of serverFieldNames) {
        const value = arbre?.[fieldName as keyof typeof arbre] ?? epaysage?.[fieldName] ?? inventaire?.[fieldName];
        result[fieldName] = getFieldLabel(fieldName, value, options);
      }

      return result as Record<string, string>;
    },

    /**
     * Get the field name label
     */
    getFieldLabelName: (serverFieldName: ArbreFieldName): string => {
      return getFieldLabelName(serverFieldName);
    },

    /**
     * Get the risque label
     */
    getRisqueLabel: (risque: any): Record<string, string> => {
      return risque?.reduce((acc: Record<string, string>, curr: any) => {

        return { ...acc, ...curr };
      }, {}) as Record<string, string>;
    }

  };
}
export function createEpaysageFieldResolver(epaysage: any): EpaysageFieldResolver {
  return {
    getFieldLabel: (serverFieldName: keyof Epaysage): string => {
      return getFieldLabelEpaysage(serverFieldName, epaysage?.[serverFieldName]);
    }
  };
}
export function createEssenceFieldResolver(essence: any): EssenceFieldResolver {
  return {
    getFieldLabel: (serverFieldName: keyof Essence): string => {
      return getFieldLabelEssence(serverFieldName, essence?.[serverFieldName]);
    }
  };
}